import {
  PetActivity,
  PetBody,
  DogStage,
  SnackLevel,
  Recipe,
  Pension,
  Cycle,
  RecalculateProductResponse,
  RecalculatedProductVariant,
} from '../../core/types';
import { IntlShape } from 'react-intl';
import { formatCycleInterval } from '@modules/subscriptions/utils/formatCycle';

export type UpdateStep = 'pet-info' | 'customize';

// 'recalculation' re-runs the calorie/product calculation using the pet's
// *current* data (no new inputs) and jumps straight to the before/after
// preview. Used by the "update-required" forced-fix flow.
// 'migration' is triggered when a customer on a LEGACY-suite product changes
// recipe / portion / delivery cycle. Instead of a plain same-product variant
// swap, we recalculate against the recommendation API
export type UpdateMode = 'weight' | 'health' | 'transition' | 'recalculation' | 'migration';

export const PET_DEFAULTS = {
  WEIGHT: 10,
  WEIGHT_MIN: 2,
  WEIGHT_MAX: 80,
  WEIGHT_STEP: 0.5,
  FALLBACK_RECIPE: ['CG'] as Recipe[],
} as const;

export interface PetInfoFormData {
  weight: number;
  activity: PetActivity;
  body: PetBody;
  snackLevel: SnackLevel;
  adultWeight?: number;
}

export interface HealthInfoFormData {
  allergies: string[];
  healthIssues: string[];
}

export interface CustomizationFormData {
  selectedRecipes: Recipe[];
  pension: Pension;
  cycle: Cycle;
}

export interface PetProfileUpdateFormData {
  petInfo: PetInfoFormData;
  customization: CustomizationFormData;
  healthInfo: HealthInfoFormData;
}

export interface PetProfileUpdateContextValue {
  mode: UpdateMode;
  step: UpdateStep;
  setStep: (step: UpdateStep) => void;

  petInfo: PetInfoFormData;
  setPetInfo: (data: PetInfoFormData) => void;

  healthInfo: HealthInfoFormData;
  setHealthInfo: (data: HealthInfoFormData) => void;

  recalculatedProduct: RecalculateProductResponse | null;

  customization: CustomizationFormData;
  setCustomization: (
    data: CustomizationFormData | ((prev: CustomizationFormData) => CustomizationFormData),
  ) => void;

  selectedVariant: RecalculatedProductVariant | null;

  currentPetInfo: PetInfoFormData;
  currentOptions: CustomizationFormData;

  isRecalculating: boolean;
  isUpdating: boolean;
  error: string | null;
  hasChanges: boolean;

  handleRecalculate: () => void;
  handleSubmit: () => void;
  handleClose: () => void;
  goBack: () => void;
  goNext: () => void;
}

// Every *Id field below holds a message id, not display text. `name`, `protein`
// and `icon` are not translated: the first two are product names and the last is
// an asset path.
export interface RecipeInfo {
  id: Recipe;
  name: string;
  /** MEAT.* message id — combine with SUBSCRIPTION.FEEDING.FRESH_WITH. */
  meatId: string;
  protein: string;
  icon: string;
}

export const RECIPE_INFO: Record<Recipe, RecipeInfo> = {
  CG: {
    id: 'CG',
    name: 'Cowgenuss',
    meatId: 'MEAT.BEEF',
    protein: 'Cowgenuss',
    icon: '/media/img/subscription/cowgenuss-circle.png',
  },
  GS: {
    id: 'GS',
    name: 'Gockelspeis',
    meatId: 'MEAT.CHICKEN',
    protein: 'Gockelspeis',
    icon: '/media/img/subscription/gockelspeis-circle.png',
  },
  RT: {
    id: 'RT',
    name: 'Ringeltanz',
    meatId: 'MEAT.PORK',
    protein: 'Ringeltanz',
    icon: '/media/img/subscription/ringeltanz-circle.png',
  },
  // TODO(WS): still using a placeholder circle icon; needs the real WS asset.
  WS: {
    id: 'WS',
    name: 'Wildschmaus',
    meatId: 'MEAT.GAME',
    protein: 'Wildschmaus',
    icon: '/media/img/subscription/ws-icon.webp',
  },
};

export const ACTIVITY_INFO: Record<PetActivity, { labelId: string; descriptionId: string }> = {
  calm: { labelId: 'PET.ACTIVITY.CALM', descriptionId: 'PET.ACTIVITY.CALM.DESC' },
  normal: { labelId: 'PET.ACTIVITY.NORMAL', descriptionId: 'PET.ACTIVITY.NORMAL.DESC' },
  active: { labelId: 'PET.ACTIVITY.ACTIVE', descriptionId: 'PET.ACTIVITY.ACTIVE.DESC' },
};

export const BODY_INFO: Record<PetBody, { labelId: string; descriptionId: string }> = {
  skinny: { labelId: 'PET.BODY.SKINNY', descriptionId: 'PET.BODY.SKINNY.DESC' },
  ideal: { labelId: 'PET.BODY.IDEAL', descriptionId: 'PET.BODY.IDEAL.DESC' },
  chunky: { labelId: 'PET.BODY.CHUNKY', descriptionId: 'PET.BODY.CHUNKY.DESC' },
};

export const PENSION_INFO: Record<Pension, { labelId: string; descriptionId: string }> = {
  V: { labelId: 'SUBSCRIPTION.PENSION.FULL', descriptionId: 'SUBSCRIPTION.PENSION.FULL.DESC' },
  H: { labelId: 'SUBSCRIPTION.PENSION.HALF', descriptionId: 'SUBSCRIPTION.PENSION.HALF.DESC' },
};

// `cycle` is a day count everywhere in the data layer; the label renders it in
// weeks via formatCycleInterval.
//
// This used to be a CYCLE_INFO map holding hand-written copy for the legacy 14
// and 28 day cycles, with a generated fallback for the dynamic plans. Once both
// went through the same formatter the two bespoke entries were byte-identical to
// the generated ones, so the map was dropped -- keeping it would only invite the
// two paths to drift. Add a per-cycle override back here if marketing ever wants
// bespoke wording again.
//
// Returns finished strings rather than ids because the description interpolates
// the formatted cycle, so it cannot be a bare id.
export const getCycleInfo = (
  intl: IntlShape,
  cycle: number,
): { label: string; description: string } => {
  const label = formatCycleInterval(intl, cycle);
  return {
    label,
    description: intl.formatMessage({ id: 'CYCLE.DELIVERY_EVERY_PLAIN' }, { cycle: label }),
  };
};

export const SNACK_LEVEL_INFO: Record<SnackLevel, { labelId: string; descriptionId: string }> = {
  none: { labelId: 'PET.SNACKS.NONE', descriptionId: 'PET.SNACKS.NONE.DESC' },
  some: { labelId: 'PET.SNACKS.SOME', descriptionId: 'PET.SNACKS.SOME.DESC' },
  lots: { labelId: 'PET.SNACKS.LOTS', descriptionId: 'PET.SNACKS.LOTS.DESC' },
};
